/**
 * useMembershipBillingPort — port-factory hook returning a stable
 * MembershipBillingPort for production composition.
 *
 * The composer (Composition.tsx) calls this once at the root and passes the
 * instance through PortsProvider as the `membershipBilling` slot. As of 041 it
 * constructs the REAL tRPC-backed reader (`TrpcMembershipBillingReader` over
 * `member.billing.listInvoices`) — the member's cookie session authenticates the
 * same-origin request, so no bearer source is wired. Mirrors `useMemberCasesPort`.
 *
 * Composition parity (041): `member.billing` is mounted on the API only when the
 * IS client is configured. Where the IS is DORMANT the reader throws and the fee
 * view renders the honest `profile.fee.unavailable` state — it does NOT silently
 * fall back to demo invoices (P7 / honest states). The `DemoMembershipBillingAdapter`
 * is retained for automated tests and local demonstration only; test harnesses pass
 * a fixture port explicitly rather than calling this factory.
 *
 * useState's lazy initialiser keeps the adapter reference stable across re-renders.
 */

import { useState } from 'react';
import type { MembershipBillingPort } from '../membership-billing/MembershipBillingPort.js';
import { TrpcMembershipBillingReader } from '../membership-billing/TrpcMembershipBillingReader.js';
import { createWebTrpcClient } from '../trpc/client.js';

export function useMembershipBillingPort(): MembershipBillingPort {
  const [port] = useState<MembershipBillingPort>(
    () => new TrpcMembershipBillingReader(createWebTrpcClient()),
  );

  return port;
}
